import { useCallback, useState } from 'react';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { appMobileProfileCatalogService } from '@/api/domains/catalog/appMobileProfiles';
import type { AppMobileProfile } from '@/api/domains/catalog/appMobileProfiles';
import type { ApiResponse } from '@/api/shared/types';


// ── Return type interface ──────────────────────────────────────────────────

export interface UseMobileAppPermissionsResult {
  profileOptions: { value: string; label: string }[];
  isProfilesLoaded: boolean;
  selectedProfileId: number | null;
  onProfileChange: (profileId: number | null) => void;
}

export function useMobileAppPermissions(): UseMobileAppPermissionsResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);

  const loadError = translate('authorization:mobileAppPermissions.loadError');

  const fetchProfiles = useCallback(
    (signal: AbortSignal) => appMobileProfileCatalogService.getProfiles(signal),
    [],
  );

  const transformProfile = useCallback(
    (profile: AppMobileProfile) => ({ value: String(profile.id), label: profile.name }),
    [],
  );

  const onLoadError = useCallback(
    (response: ApiResponse<AppMobileProfile[]> | null) => { showApiError(response, loadError); },
    [showApiError, loadError],
  );

  const {
    options: profileOptions,
    isLoaded: isProfilesLoaded,
  } = useCatalogLoader<AppMobileProfile>(
    fetchProfiles,
    transformProfile,
    [],
    false,
    onLoadError,
  );

  const onProfileChange = useCallback((profileId: number | null) => {
    setSelectedProfileId(profileId);
    // El perfil seleccionado alimentará los paneles de asignados/sin asignar (ticket posterior).
  }, []);

  return { profileOptions, isProfilesLoaded, selectedProfileId, onProfileChange };
}

export default useMobileAppPermissions;
